import { useEffect, useMemo, useRef, useState, type ComponentType } from 'react';
import * as Icons from 'lucide-react';
import { ChevronDown } from 'lucide-react';
import { getExtendedQuickActions, type QuickAction } from '@court-copilot/prompts';
import { Button } from '@/components/ui';

const CATEGORY_LABELS: Record<QuickAction['category'], string> = {
  tactical: 'Tactical',
  evidential: 'Evidential',
  procedural: 'Procedural',
  strategic: 'Strategic',
};

const CATEGORY_ORDER: ReadonlyArray<QuickAction['category']> = [
  'evidential',
  'procedural',
  'tactical',
  'strategic',
];

interface MoreActionsMenuProps {
  disabled: boolean;
  onSelect: (action: QuickAction) => void;
}

export function MoreActionsMenu({ disabled, onSelect }: MoreActionsMenuProps) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const grouped = useMemo(() => {
    const all = getExtendedQuickActions();
    return CATEGORY_ORDER.map((cat) => ({
      category: cat,
      label: CATEGORY_LABELS[cat],
      actions: all.filter((a) => a.category === cat),
    })).filter((group) => group.actions.length > 0);
  }, []);

  useEffect(() => {
    if (!open) {
      return;
    }
    function onMouseDown(event: MouseEvent): void {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    function onKey(event: KeyboardEvent): void {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={wrapperRef} className="relative">
      <Button
        variant="secondary"
        size="sm"
        className="w-full justify-between"
        rightSlot={<ChevronDown className="h-3.5 w-3.5" />}
        disabled={disabled}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((c) => !c)}
      >
        More actions
      </Button>

      {open ? (
        <div className="bg-stone-925 absolute left-0 right-0 top-full z-30 mt-2 max-h-[60vh] overflow-y-auto rounded-md border border-stone-800 shadow-lg shadow-stone-950/80">
          {grouped.map((group) => (
            <div key={group.category} className="border-b border-stone-800/60 last:border-b-0">
              <p className="bg-stone-900/40 px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] text-stone-500">
                {group.label}
              </p>
              {group.actions.map((action) => {
                const Icon = resolveIcon(action.icon);
                return (
                  <button
                    key={action.id}
                    type="button"
                    onClick={() => {
                      onSelect(action);
                      setOpen(false);
                    }}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-stone-200 hover:bg-stone-900"
                  >
                    {Icon ? <Icon className="h-4 w-4 text-amber-500" /> : null}
                    <span className="truncate">{action.label}</span>
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

type IconComponent = ComponentType<{ className?: string }>;

function resolveIcon(name: string): IconComponent | null {
  const all = Icons as unknown as Record<string, unknown>;
  const candidate = all[name];
  if (typeof candidate === 'function' || typeof candidate === 'object') {
    return candidate as IconComponent;
  }
  return null;
}
